import React, { useState, useEffect, useCallback } from 'react';
import { View, ScrollView, StyleSheet, Alert, FlatList, Modal, TouchableOpacity } from 'react-native';
import { Text, Card, Avatar, useTheme, ActivityIndicator, Button, Divider, Chip, List, IconButton, Snackbar, Portal, Dialog } from 'react-native-paper';
import { getUserById, toggleUserStatus, updateUser } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';

const UserDetailScreen = ({ route, navigation }) => {
  const theme = useTheme();
  const { userId } = route.params || {};
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Dialog için state
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      
      if (!userId) {
        setSnackbarMessage('Geçersiz kullanıcı ID');
        setSnackbarVisible(true);
        navigation.goBack();
        return;
      }
      
      const response = await getUserById(userId);
      
      if (response.success) {
        setUser(response.data);
      } else {
        setSnackbarMessage('Kullanıcı bilgileri alınırken bir hata oluştu');
        setSnackbarVisible(true);
        navigation.goBack();
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setSnackbarMessage('Kullanıcı bilgileri alınırken bir hata oluştu');
      setSnackbarVisible(true);
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  // Yaş hesaplama
  const getAge = (birthdate) => {
    try {
      const birthDate = new Date(birthdate);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (error) {
      console.error('Error calculating age:', error);
      return 'Bilinmiyor';
    }
  };

  // Ücret formatını TL olarak göster
  const formatPrice = (price) => {
    return parseFloat(price).toFixed(2) + ' ₺';
  };

  // Ziyaret durumunu Türkçe olarak göster
  const getStatusText = (status) => {
    switch (status) {
      case 'Active': return 'Aktif';
      case 'Completed': return 'Tamamlandı';
      case 'Cancelled': return 'İptal Edildi';
      case 'WaitingForApproval': return 'Onay Bekliyor';
      default: return status || '-';
    }
  };
  
  // Ziyaret süresini hesapla (aktif ziyaretler için)
  const calculateElapsedTime = (startTime) => {
    if (!startTime) return 0;
    
    const start = new Date(startTime);
    const now = new Date();
    const elapsedMinutes = Math.floor((now - start) / (1000 * 60));
    
    return elapsedMinutes;
  };
  
  // Ziyaret kartını render et
  const renderVisitItem = ({ item: activity }) => {
    // Çocuk bilgisini bul
    const childInfo = user.children && user.children.find(child => child && child.id === activity.child_id);
    const childName = childInfo ? `${childInfo.first_name} ${childInfo.last_name}` : "Bilinmiyor";
    
    return (
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.visitHeader}>
            <Avatar.Icon 
              size={50} 
              icon={childInfo && childInfo.gender === 'male' ? "human-male-boy" : "human-female-girl"} 
              color="white"
              style={{ backgroundColor: childInfo && childInfo.gender === 'male' ? '#2196F3' : '#E91E63' }}
            />
            
            <View style={styles.visitInfo}>
              <Text style={styles.childName}>{childName}</Text>
              
              <Chip 
                icon="clock-check-outline" 
                style={[
                  styles.statusChip, 
                  { backgroundColor: activity.status === 'Active' ? '#4CAF50' : 
                                  activity.status === 'Completed' ? '#2196F3' : 
                                  activity.status === 'Cancelled' ? '#F44336' : 
                                  '#FF9800' }
                ]}
                textStyle={{ color: 'white' }}
              >
                {getStatusText(activity.status)}
              </Chip>
            </View>
          </View>
          
          <Divider style={styles.divider} />
          
          <View style={styles.visitDetails}>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Giriş:</Text>
              <Text style={styles.detailValue}>{activity.start_time ? formatDateTime(activity.start_time) : '-'}</Text>
            </View>
            
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Çıkış:</Text>
              <Text style={styles.detailValue}>{activity.end_time ? formatDateTime(activity.end_time) : 'Devam Ediyor'}</Text>
            </View>
            
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Süre:</Text>
              <Text style={styles.detailValue}>
                {activity.end_time 
                  ? `${Math.floor(activity.duration / 60)} saat ${activity.duration % 60} dakika` 
                  : activity.start_time ? `${Math.floor(calculateElapsedTime(activity.start_time) / 60)} saat ${calculateElapsedTime(activity.start_time) % 60} dakika (devam ediyor)` : '-'}
              </Text>
            </View>
            
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Tutar:</Text>
              <Text style={[styles.detailValue, styles.priceValue]}>
                {formatPrice(activity.price || '0.00')}
              </Text>
            </View>
            
            {activity.notes && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Not:</Text>
                <Text style={styles.detailValue}>{activity.notes}</Text>
              </View>
            )}
          </View>
          
          <View style={styles.actionButtons}>
            <Button 
              mode="text"
              icon="eye"
              onPress={() => navigateToVisitDetail(activity)}
              color="rgb(125, 76, 236)"
            >
              Detaylar
            </Button>
          </View>
        </Card.Content>
      </Card>
    );
  };

  // Kullanıcı durumunu değiştir (aktif/pasif) - Dialog göster
  const confirmToggleStatus = () => {
    setStatusDialogVisible(true);
  };
  
  // Dialog kapat
  const hideStatusDialog = () => {
    setStatusDialogVisible(false);
  };
  
  // Kullanıcı durumu değiştirme işlemini gerçekleştir
  const handleToggleStatus = async () => {
    try {
      hideStatusDialog();
      setActionLoading(true);
      
      const newStatus = !user.is_active;
      //console.log(`Toggling user status for user ID ${userId} to ${newStatus}`);
      
      // Axios ile doğrudan API çağrısı (test amaçlı)
      const response = await toggleUserStatus(userId, newStatus);
      //console.log('Toggle status response:', response);
      
      if (response.success) {
        // Kullanıcı state'ini güncelle
        setUser(prevUser => ({
          ...prevUser,
          is_active: newStatus
        }));
        
        setSnackbarMessage(`Kullanıcı ${newStatus ? 'aktif' : 'pasif'} duruma getirildi`);
        setSnackbarVisible(true);
      } else {
        setSnackbarMessage(response.message || 'Durum değiştirme işlemi başarısız oldu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      setSnackbarMessage('Kullanıcı durumu değiştirilirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Kullanıcı düzenleme sayfasına git
  const navigateToEditUser = () => {
    navigation.navigate('UserForm', { user });
  };

  // Çocuk detay sayfasına git
  const navigateToChildDetail = (child) => {
    navigation.navigate('ChildrenManagement', {
      screen: 'ChildDetail',
      params: {
        childId: child.id,
        childName: `${child.first_name} ${child.last_name}`
      }
    });
  };

  // Ziyaret detayına git
  const navigateToVisitDetail = (activity) => {
    navigation.navigate('VisitsManagement', {
      screen: 'VisitHistory',
      params: {
        childId: activity.child_id
      }
    });
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
        <Text style={styles.loadingText}>Kullanıcı bilgileri yükleniyor...</Text>
      </View>
    );
  }

  if (!user) {
    return (
      <View style={styles.errorContainer}>
        <MaterialCommunityIcons name="account-alert" size={80} color="#BDBDBD" />
        <Text style={styles.errorText}>Kullanıcı bilgileri bulunamadı</Text>
        <Button mode="contained" onPress={() => navigation.goBack()}>
          Geri Dön
        </Button>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.headerCard}>
        <Card.Content style={styles.headerContent}>
          <View style={styles.avatarContainer}>
            <Avatar.Text 
              size={80} 
              label={
                (user.name && user.surname) 
                  ? `${user.name.charAt(0)}${user.surname.charAt(0)}` 
                  : user.name 
                    ? user.name.charAt(0)
                    : user.username?.charAt(0) || '?'
              }
              color="white" 
              style={{ backgroundColor: user.is_active ? 'rgb(125, 76, 236)' : '#757575' }}
            />
          </View>
          
          <View style={styles.userNameContainer}>
            <View style={styles.nameAndStatusContainer}>
              <Text style={styles.userName}>
                {(user.name && user.surname) 
                  ? `${user.name.charAt(0).toUpperCase() + user.name.slice(1)} ${user.surname.charAt(0).toUpperCase() + user.surname.slice(1)}` 
                  : user.name 
                    ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
                    : user.username}
              </Text>
              <Chip 
                icon={user.is_active ? "check-circle" : "close-circle"} 
                style={[styles.statusChip, {backgroundColor: user.is_active ? '#E8F5E9' : '#FFEBEE'}]}
              >
                {user.is_active ? 'Aktif' : 'Pasif'}
              </Chip>
              <Chip 
                icon={user.is_admin ? "shield-account" : "account"} 
                style={[styles.roleChip, {backgroundColor: user.is_admin ? '#E1F5FE' : '#F3E5F5'}]}
              >
                {user.is_admin ? 'Admin' : 'Kullanıcı'}
              </Chip>
            </View>
            <Text style={styles.userUsername}>@{user.username}</Text>
            
            
          </View>
        </Card.Content>
        
        <View style={styles.actionButtonsContainer}>
  {user.role !== 'admin' && (
    <Button 
      mode="contained" 
      icon={user.is_active ? "account-off" : "account-check"} 
      onPress={confirmToggleStatus}
      style={[styles.actionButton, user.is_active ? styles.deactivateButton : styles.activateButton]}
      labelStyle={{ color: 'white' }}
      loading={actionLoading}
      disabled={actionLoading}
    >
      {user.is_active ? 'Pasif Yap' : 'Aktif Yap'}
    </Button>
  )}

  <Button 
    mode="outlined" 
    icon="account-edit" 
    onPress={navigateToEditUser}
    style={styles.actionButton}
    disabled={actionLoading}
  >
    Düzenle
  </Button>
</View>
      </Card>
      
      <Card style={styles.detailsCard}>
        <Card.Title title="Kullanıcı Hesabı" left={(props) => <MaterialCommunityIcons {...props} name="account" size={24} color="rgb(125, 76, 236)" />} />
        <Card.Content>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>ID:</Text>
            <Text style={styles.detailValue}>{user.id}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Kullanıcı Adı:</Text>
            <Text style={styles.detailValue}>{user.username}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>E-posta:</Text>
            <Text style={styles.detailValue}>{user.email || '-'}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Telefon:</Text>
            <Text style={styles.detailValue}>{user.phone_number || '-'}</Text>
          </View>
          
          <Divider style={styles.divider} />
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Üyelik Türü:</Text>
            <View style={styles.chipContainer}>
              <Chip icon={user.role === 'admin' ? "shield-account" : "account"}>
                {user.role === 'admin' ? 'Admin' : 'Standart Kullanıcı'}
              </Chip>
            </View>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Durum:</Text>
            <View style={styles.chipContainer}>
              <Chip icon={user.is_active ? "check-circle" : "close-circle"}>
                {user.is_active ? 'Aktif' : 'Pasif'}
              </Chip>
            </View>
          </View>
        </Card.Content>
      </Card>
      
      <Card style={styles.detailsCard}>
        <Card.Title title="Çocuklar" left={(props) => <MaterialCommunityIcons {...props} name="human-male-child" size={24} color="rgb(125, 76, 236)" />} />
        <Card.Content>
          {user.children && user.children.length > 0 ? (
            user.children.map((child) => (
              <List.Item
                key={child.id.toString()}
                title={`${child.first_name} ${child.last_name}`}
                description={child.birthdate ? `${getAge(child.birthdate)} yaşında, ${child.gender === 'male' ? 'Erkek' : 'Kız'}` : undefined}
                left={props => (
                  <Avatar.Icon 
                    {...props} 
                    icon={child.gender === 'male' ? "human-male-boy" : "human-female-girl"} 
                    color="white"
                    style={{ backgroundColor: 'rgb(125, 76, 236)' }}
                  />
                )}
                right={props => (
                  <IconButton 
                    {...props} 
                    icon="eye" 
                    onPress={() => navigateToChildDetail(child)} 
                  />
                )}
              />
            ))
          ) : (
            <Text style={styles.emptyListText}>Henüz çocuk kaydı bulunmuyor</Text>
          )}
        </Card.Content>
      </Card>
      
      <Card style={styles.historyCard}>
        <Card.Title title="Ziyaret Geçmişi" left={(props) => <MaterialCommunityIcons {...props} name="calendar-clock" size={24} color="rgb(125, 76, 236)" />} />
        <Card.Content>
          {user.createdActivities && user.createdActivities.length > 0 ? (
            <FlatList
              data={user.createdActivities}
              keyExtractor={(item) => item.id.toString()}
              renderItem={renderVisitItem}
              scrollEnabled={false} // Scroll içinde scroll engelle
              contentContainerStyle={styles.listContent}
            />
          ) : (
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons name="calendar-blank" size={60} color="#BDBDBD" />
              <Text style={styles.emptyText}>Ziyaret geçmişi bulunamadı</Text>
            </View>
          )}
        </Card.Content>
      </Card>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
      >
        {snackbarMessage}
      </Snackbar>
      
      {/* Kullanıcı durumu değiştirme modal'ı */}
      <Modal
        visible={statusDialogVisible}
        transparent={true}
        onRequestClose={hideStatusDialog}
      >
        <View style={styles.modalContainer}>
  <View style={styles.errorModalContent}>
    <MaterialCommunityIcons 
      name="alert-circle-outline" 
      size={50} 
      color="#FF9800"
      style={styles.errorIcon} 
    />
    
    <Text style={styles.errorModalTitle}>Kullanıcı Durumunu Değiştir</Text>
    <Text style={styles.errorModalText}>
      {user && `${user.name || user.username} kullanıcısını ${user.is_active ? 'pasif' : 'aktif'} yapmak istediğinize emin misiniz?`}
    </Text>
    
    <View style={styles.errorModalButtonContainer}>
      {/* Sadece admin değilse göster */}
      {user?.role !== 'admin' && (
        <Button 
          mode="contained" 
          onPress={handleToggleStatus}
          style={[styles.modalButton, { backgroundColor: user?.is_active ? '#F44336' : '#4CAF50' }]}
          icon={user?.is_active ? "account-off" : "account-check"}
        >
          {user?.is_active ? 'Pasif Yap' : 'Aktif Yap'}
        </Button>
      )}

      <Button 
        mode="outlined" 
        onPress={hideStatusDialog}
        style={styles.modalButton}
      >
        İptal
      </Button>
    </View>
  </View>
</View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  errorText: {
    fontSize: 18,
    marginTop: 16,
    marginBottom: 24,
    color: '#757575',
  },
  headerCard: {
    margin: 12,
    elevation: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarContainer: {
    marginRight: 10,
  },
  nameAndStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  statusChip: {
    height: 28,
    marginLeft: 10,
  },
  userNameContainer: {
    marginLeft: 0,
    flex: 1,
  },
  userName: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  userUsername: {
    fontSize: 14,
    color: '#757575',
    marginBottom: 6,
  },
  roleChip: {
    height: 28,
    alignSelf: 'flex-start',
    marginTop: 5,
  },
  userRoleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: 5,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 12,
    gap: 8,
  },
  actionButton: {
    flex: 1,
    marginHorizontal: 4,
  },
  deactivateButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  activateButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  detailsCard: {
    margin: 12,
    marginTop: 0,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  divider: {
    marginVertical: 12,
  },
  detailRow: {
    flexDirection: 'row',
    paddingVertical: 4,
  },
  detailLabel: {
    width: 120,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  detailValue: {
    flex: 1,
  },
  emptyListText: {
    textAlign: 'center',
    paddingVertical: 16,
    color: '#757575',
    fontStyle: 'italic',
  },
  viewAllButton: {
    marginTop: 8,
  },
  emptyStateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#757575',
    marginTop: 8,
  },
  // Modal stilleri
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  errorModalContent: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 8,
    padding: 20,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
    borderColor: 'rgb(125, 76, 236)',
    borderWidth: 1,
  },
  errorIcon: {
    marginBottom: 15,
  },
  errorModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(125, 76, 236)',
    textAlign: 'center',
  },
  errorModalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  errorModalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  modalButton: {
    marginHorizontal: 5,
    borderRadius: 4,
    marginTop: 10,
    flex: 1, // Butonun mevcut alanı doldurmasını sağlar
    minWidth: 120, // Minimum genişlik garantisi
    fontSize: 16,
    color: '#757575',
    marginTop: 16,
  },
  noDataText: {
    textAlign: 'center',
    color: '#757575',
    marginVertical: 16,
  },
  historyCard: {
    marginTop: 16,
    marginBottom: 16,
  },
  card: {
    marginBottom: 10,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  visitHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  visitInfo: {
    marginLeft: 15,
    flex: 1,
  },
  childName: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  statusChip: {
    alignSelf: 'flex-start',
    marginTop: 5,
  },
  divider: {
    marginVertical: 10,
  },
  visitDetails: {
    marginBottom: 10,
  },
  detailRow: {
    flexDirection: 'row',
    paddingVertical: 3,
  },
  detailLabel: {
    width: 80,
    fontWeight: 'bold',
    color: '#616161',
  },
  detailValue: {
    flex: 1,
    color: '#212121',
  },
  priceValue: {
    fontWeight: '600',
    color: 'rgb(125, 76, 236)',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 12,
  },
  detailButton: {
    marginLeft: 8,
  },
  listContent: {
    paddingVertical: 5,
  },
  emptyContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  emptyText: {
    marginTop: 10,
    fontSize: 16,
    color: '#757575',
    textAlign: 'center',
  },
});

export default UserDetailScreen;
